import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Dialog, DialogHeader } from "@astryxdesign/core/Dialog";
import { Layout, LayoutContent, LayoutFooter, HStack } from "@astryxdesign/core/Layout";
import { FormLayout } from "@astryxdesign/core/FormLayout";
import { TextInput } from "@astryxdesign/core/TextInput";
import { Selector } from "@astryxdesign/core/Selector";
import { Button } from "@astryxdesign/core/Button";
import { Banner } from "@astryxdesign/core/Banner";
import { api } from "../../lib/api";
import type { CreateUserInput, UpdateUserInput, User } from "../../lib/api";

interface UserFormValues {
  username: string;
  email: string;
  displayName: string;
  password: string;
  roleId: string;
}

type FieldErrors = Partial<Record<keyof UserFormValues, string>>;

const emptyValues: UserFormValues = {
  username: "",
  email: "",
  displayName: "",
  password: "",
  roleId: "",
};

interface UserFormDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  /** Present for edit, absent for create. */
  editing: User | null;
  isSaving: boolean;
  onCreate: (input: CreateUserInput) => Promise<unknown>;
  onUpdate: (id: string, input: UpdateUserInput) => Promise<unknown>;
}

// One dialog handles both create and edit, same reasoning as
// CameraFormDialog: the two backend operations differ in one meaningful way
// (create sets a password; edit never does — that goes through the separate
// reset-password flow, since it is a state transition with its own
// authorization story) and everything else about the form is identical.
export function UserFormDialog({ isOpen, onOpenChange, editing, isSaving, onCreate, onUpdate }: UserFormDialogProps) {
  const [values, setValues] = useState<UserFormValues>(emptyValues);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [requestError, setRequestError] = useState<string | null>(null);

  // Roles are fetched here (not passed down from UsersPage) because this
  // dialog is the only place that needs the full list — the table itself
  // only ever displays the role a user already has.
  const { data: roles } = useQuery({ queryKey: ["roles"], queryFn: api.roles.list, enabled: isOpen });

  useEffect(() => {
    if (!isOpen) return;
    setFieldErrors({});
    setRequestError(null);
    if (editing) {
      setValues({
        username: editing.username,
        email: editing.email,
        displayName: editing.display_name,
        password: "",
        roleId: editing.role.id,
      });
    } else {
      setValues(emptyValues);
    }
  }, [isOpen, editing]);

  // Default a new user to the Viewer-equivalent role once roles load, if
  // nothing has been picked yet — picking the least-privileged role as the
  // default rather than leaving it unset or defaulting to admin.
  useEffect(() => {
    if (!isOpen || editing || values.roleId || !roles || roles.length === 0) return;
    const leastPrivileged = roles.find((r) => r.permissions.length === 1) ?? roles[0];
    if (leastPrivileged) setValues((v) => ({ ...v, roleId: leastPrivileged.id }));
  }, [isOpen, editing, roles, values.roleId]);

  const set = <K extends keyof UserFormValues>(key: K) => (value: UserFormValues[K]) =>
    setValues((v) => ({ ...v, [key]: value }));

  const statusFor = (field: keyof UserFormValues) =>
    fieldErrors[field] ? ({ type: "error", message: fieldErrors[field] } as const) : undefined;

  const handleSubmit = async () => {
    setRequestError(null);

    const errors: FieldErrors = {};
    if (!values.username.trim()) errors.username = "Username is required.";
    if (!values.email.trim()) errors.email = "Email is required.";
    if (!editing && !values.password.trim()) errors.password = "Password is required.";
    if (!values.roleId) errors.roleId = "A role is required.";
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    try {
      if (editing) {
        await onUpdate(editing.id, {
          username: values.username.trim(),
          email: values.email.trim(),
          display_name: values.displayName.trim(),
          role_id: values.roleId,
          version: editing.version,
        });
      } else {
        await onCreate({
          username: values.username.trim(),
          email: values.email.trim(),
          display_name: values.displayName.trim(),
          password: values.password,
          role_id: values.roleId,
        });
      }
      onOpenChange(false);
    } catch (err) {
      setRequestError(err instanceof Error ? err.message : "Something went wrong.");
    }
  };

  // No <form> element wraps this dialog's fields — see CameraFormDialog's
  // own comment on why TextInput's onEnter is used instead.
  const submitOnEnter = () => {
    if (!isSaving) void handleSubmit();
  };

  return (
    <Dialog isOpen={isOpen} onOpenChange={onOpenChange} purpose="form" width={480}>
      <Layout
        header={
          <DialogHeader
            title={editing ? `Edit ${editing.username}` : "Add user"}
            subtitle={editing ? undefined : "They'll sign in with this username or email and password."}
            onOpenChange={onOpenChange}
          />
        }
        content={
          <LayoutContent>
            <FormLayout>
              <TextInput
                label="Username"
                value={values.username}
                onChange={set("username")}
                status={statusFor("username")}
                isRequired
                hasAutoFocus
                onEnter={submitOnEnter}
              />
              <TextInput
                label="Email"
                value={values.email}
                onChange={set("email")}
                status={statusFor("email")}
                isRequired
                onEnter={submitOnEnter}
              />
              <TextInput
                label="Display name"
                value={values.displayName}
                onChange={set("displayName")}
                onEnter={submitOnEnter}
              />
              {!editing && (
                <TextInput
                  label="Password"
                  type="password"
                  description="They can change this themselves after signing in."
                  value={values.password}
                  onChange={set("password")}
                  status={statusFor("password")}
                  isRequired
                  onEnter={submitOnEnter}
                />
              )}
              <Selector
                label="Role"
                value={values.roleId}
                onChange={set("roleId")}
                options={(roles ?? []).map((r) => ({ value: r.id, label: r.name }))}
                placeholder="Choose a role"
                status={statusFor("roleId")}
                isRequired
              />
              {requestError && <Banner status="error" title="Couldn't save user" description={requestError} />}
            </FormLayout>
          </LayoutContent>
        }
        footer={
          <LayoutFooter>
            <HStack gap={2} hAlign="end">
              <Button label="Cancel" variant="ghost" onClick={() => onOpenChange(false)} isDisabled={isSaving} />
              <Button
                label={editing ? "Save changes" : "Add user"}
                variant="primary"
                clickAction={handleSubmit}
                isLoading={isSaving}
              />
            </HStack>
          </LayoutFooter>
        }
      />
    </Dialog>
  );
}
